/**
 * mathHelpers — Small composable numeric helpers used across demos.
 *
 * Every function here was open-coded in 2+ demos before this module
 * landed (`Math.max(min, Math.min(max, x))` in 9+ places, `lerp` in 3,
 * `lerpColor` in 2). Centralizing them removes the room for inconsistent
 * boundary behavior (e.g. one demo clamping `[min, max)` vs another
 * `[min, max]`) and lets a single audit cover the contract.
 *
 * All functions are pure, allocation-free where possible, and safe on
 * non-finite input — `NaN` propagates rather than producing a silently
 * wrong number. None of them snap to integers; callers that need
 * rounding should compose with `Math.round`.
 *
 * Library evaluation (round 5): we considered replacing this module
 * with `lodash-es` (`clamp`), `remeda` (`clamp`), or a color-only
 * library like `colord`. The basic math is two-line ECMAScript per
 * function — pulling a dependency for `lerp` would not improve
 * clarity, and `lodash-es/clamp` matches our semantics but costs
 * more than the function it replaces. For the color helpers, the
 * three demos that consume them work in `[0, 255]` RGB tuples
 * because that is the Canvas `ImageData` representation; richer
 * libraries (`colord`, `culori`) handle P3 / OKLCH / alpha — fine
 * concerns we do not have. If we ever need named colors, gradients
 * across color spaces, or alpha compositing, switch to `colord`.
 *
 * Tracking issue for the broader library evaluation: #1921.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

/** Clamp `value` to the closed interval `[min, max]`. */
export function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/** Linear interpolation: `lerp(a, b, 0) === a`, `lerp(a, b, 1) === b`. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Inverse of {@link lerp}: returns the `t` such that
 * `lerp(a, b, t) === value`. Returns `0` when `a === b` to avoid a
 * divide-by-zero spike at the discontinuity.
 */
export function inverseLerp(a: number, b: number, value: number): number {
  if (a === b) return 0;
  return (value - a) / (b - a);
}

/**
 * Affine remap of `value` from `[fromMin, fromMax]` to `[toMin, toMax]`.
 * Does **not** clamp; callers that want clamped output should compose
 * with {@link clamp}.
 */
export function mapRange(
  value: number,
  fromMin: number,
  fromMax: number,
  toMin: number,
  toMax: number,
): number {
  return lerp(toMin, toMax, inverseLerp(fromMin, fromMax, value));
}

/** RGB triple in the 0..255 integer range (typed for clarity). */
export type RgbTriple = readonly [number, number, number];

/**
 * Linearly interpolate between two RGB triples. Each channel is floored
 * after the interpolation and then clamped to `[0, 255]`, so the
 * return is guaranteed to be a valid integer-channel RGB triple even
 * if the caller passes `t` outside `[0, 1]` or endpoints outside the
 * 0..255 range. The flooring matches what every previous open-coded
 * copy was doing.
 */
export function lerpColor(a: RgbTriple, b: RgbTriple, t: number): RgbTriple {
  return [
    clamp(Math.floor(lerp(a[0], b[0], t)), 0, 255),
    clamp(Math.floor(lerp(a[1], b[1], t)), 0, 255),
    clamp(Math.floor(lerp(a[2], b[2], t)), 0, 255),
  ];
}

/** Format an RGB triple as a `rgb(r, g, b)` CSS color. */
export function rgbToCss(triple: RgbTriple): string {
  return `rgb(${triple[0]}, ${triple[1]}, ${triple[2]})`;
}

/** Format an RGB triple as a `#rrggbb` hex color. */
export function rgbToHex(triple: RgbTriple): string {
  const toHex = (n: number): string => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  return `#${toHex(triple[0])}${toHex(triple[1])}${toHex(triple[2])}`;
}

/**
 * Parse a `#rrggbb` or `#rgb` hex string into an RGB triple. Returns
 * `null` on any malformed input — never throws, never returns a
 * "wrong" color. Whitespace and case are tolerated.
 */
export function hexToRgb(hex: string): RgbTriple | null {
  if (typeof hex !== "string") return null;
  const trimmed = hex.trim().toLowerCase();
  // Capture group is always defined when the match succeeds — but the
  // type system can't see that without an `!` assertion, which the
  // workspace lint forbids. Strip the leading `#` and use the body
  // directly; the same regex shape gates the length check.
  const body = trimmed.startsWith("#") ? trimmed.slice(1) : null;
  if (body === null) return null;
  if (!/^[0-9a-f]+$/.test(body)) return null;
  if (body.length === 3) {
    const r = body.charAt(0);
    const g = body.charAt(1);
    const b = body.charAt(2);
    return [Number.parseInt(r + r, 16), Number.parseInt(g + g, 16), Number.parseInt(b + b, 16)];
  }
  if (body.length === 6) {
    return [
      Number.parseInt(body.slice(0, 2), 16),
      Number.parseInt(body.slice(2, 4), 16),
      Number.parseInt(body.slice(4, 6), 16),
    ];
  }
  return null;
}
